import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, UserPlus } from "lucide-react";
import { listPrimeContactsWithoutRfp, type PrimeContactWithoutRfp } from "./api/CrmApi";
import { apiErrorMessage } from "./apiError";
import AddPrimeContactModal from "./ui/AddPrimeContactModal";
import { formatDate } from "./ui/crmUi";
import PageHeader from "shared/PageHeader";
import StatusPill from "shared/StatusPill";
import Button from "shared/Button";
import Alert from "shared/Alert";
import PagerNav from "shared/PagerNav";
import { FILLED_INPUT, TABLE_SHELL, TBODY_ROW, TD, TH, THEAD_ROW } from "shared/tableStyles";
import { useUrlState, useUrlNumberState, useUpdateEffect, rowLinkState } from "shared/listNavigation";

interface Props {
  /** Leave this view (back to the per-RFP list). */
  onShowAppearances: () => void;
  onAddContact: (created: { id: number }) => void;
}

/**
 * Contact people with no RFP appearance yet — mostly those added by hand, which the per-RFP list (one row per
 * appearance) never shows. Click a name for the person's page.
 */
export default function PeopleWithoutRfpView({ onShowAppearances, onAddContact }: Props) {
  const location = useLocation();
  const crmBasePath = location.pathname.startsWith("/employee") ? "/employee/crm" : "/admin/crm";
  const [page, setPage] = useUrlNumberState("page", 1);
  const [search, setSearch] = useUrlState("q", "");
  const [searchInput, setSearchInput] = useState(search);
  const [items, setItems] = useState<PrimeContactWithoutRfp[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  useUpdateEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    listPrimeContactsWithoutRfp({ search: search || undefined, page })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotalCount(res.total_count);
        setTotalPages(res.total_pages);
      })
      .catch((e) => !cancelled && setError(apiErrorMessage(e, "Failed to load contacts")))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [search, page]);

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      {addOpen && (
        <AddPrimeContactModal onClose={() => setAddOpen(false)} onSaved={(created) => { setAddOpen(false); onAddContact(created); }} />
      )}
      <PageHeader
        title="Prime Contacts"
        badge={<StatusPill tone="neutral" size="sm">{totalCount} with no RFP yet</StatusPill>}
        description="People on file with no active RFP record — for example contacts added by hand, or whose RFP was re-synced without them. They are available to outreach like any other verified contact."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={onShowAppearances}>Show RFP contacts</Button>
            <Button variant="primary" size="sm" onClick={() => setAddOpen(true)} title="Add a contact person to a company by hand">
              <UserPlus className="w-3.5 h-3.5" /> Add contact
            </Button>
          </>
        }
      />
      <div className="relative w-72">
        <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name, email or company..."
          className={`w-full pl-8 pr-3 py-1.5 h-9 ${FILLED_INPUT}`}
        />
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className={`${TABLE_SHELL} overflow-x-auto flex-1 min-h-0`}>
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className={THEAD_ROW}>
              <th className={TH}>Contact</th>
              <th className={TH}>Company</th>
              <th className={TH}>Email</th>
              <th className={TH}>Title</th>
              <th className={TH}>Added</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className={TBODY_ROW}>
                <td className={`${TD} text-sm`}>
                  <Link to={`${crmBasePath}/prime-contacts/${p.id}`} state={rowLinkState(location)} className="text-primary hover:underline">
                    {[p.first_name, p.last_name].filter(Boolean).join(" ") || p.primary_email}
                  </Link>
                </td>
                <td className={`${TD} text-sm`}>
                  <Link to={`${crmBasePath}/companies/${p.prime_company_id}`} state={rowLinkState(location)} className="text-primary hover:underline">
                    {p.company_name || p.company_domain || `Company #${p.prime_company_id}`}
                  </Link>
                </td>
                <td className={`${TD} text-sm text-muted-foreground`}>{p.primary_email}</td>
                <td className={`${TD} text-sm text-foreground`}>{p.title || "—"}</td>
                <td className={`${TD} text-sm text-muted-foreground`}>{p.created_at ? formatDate(p.created_at) : "—"}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  {search ? "No matching contacts." : "Everyone on file is on at least one RFP. Use Add contact to add someone by hand."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && <PagerNav page={page} totalPages={totalPages} onChange={setPage} />}
    </div>
  );
}
